import { AgentListItem } from "@/types/agent";
import { apiClient } from "@/lib/apiClient";
import { getLocalAgents, getLocalAgentById } from "./localAgentService";
import { getElevenLabsAgentsConverted, getElevenLabsAgentByIdConverted } from "./elevenLabsAgentService";

/**
 * Fetches the combined list of local and ElevenLabs agents
 */
export const getAgents = async (): Promise<AgentListItem[]> => {
  try {
    const [localAgents, elevenLabsAgents] = await Promise.all([
      getLocalAgents(),
      getElevenLabsAgentsConverted(),
    ]);
    
    return [...localAgents, ...elevenLabsAgents];
  } catch (error) {
    console.error("Error fetching combined agents:", error);
    return await getLocalAgents();
  }
};

/**
 * Get a specific agent by ID from any source
 */
export const getAgentById = async (agentId: string): Promise<AgentListItem | null> => {
  try {
    if (!agentId) return null;
    
    if (agentId.startsWith('el_')) {
      return await getElevenLabsAgentByIdConverted(agentId);
    }
    
    return await getLocalAgentById(agentId);
  } catch (error) {
    console.error("Error fetching agent by ID:", error);
    
    return {
      id: agentId,
      name: "AI Assistant",
      role: "General Assistant", 
      goal: "Assist with general tasks and information retrieval",
      active: true,
      source: "local" as const,
    };
  }
};

/**
 * Returns a default agent to use when none is selected
 */
export const getDefaultAgent = async (): Promise<AgentListItem> => {
  try {
    const defaultAgent = await apiClient.get('/agents/default');
    if (defaultAgent) {
      return {
        ...defaultAgent,
        source: "local" as const,
      };
    }
    
    const agents = await getAgents();
    
    if (!Array.isArray(agents) || agents.length === 0) {
      throw new Error("No agents available");
    }
    
    const markedDefault = agents.find(agent => agent.isDefault);
    
    if (!markedDefault) {
      const activeAgent = agents.find(agent => agent.active);
      if (activeAgent) return activeAgent;
      return agents[0];
    }
    
    return markedDefault;
  } catch (error) {
    console.error("Error getting default agent:", error);
    
    return {
      id: "agent-001",
      name: "Personal Assistant",
      role: "General Assistant",
      goal: "Assist with general tasks and information retrieval",
      active: true,
      source: "local" as const,
    };
  }
};
